import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiErrorText, apiRequest, apiFormRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyTournament.css';

/* What is actually played — the games a company invented, and which of them are on this day.

   ★★ They write their own; they do not pick ours. The owner's call: a customer describes their
   game, with its rules, and it lands in THEIR library, ready for the next event. Our own
   catalogue is not offered here — it is ours, and it is not commercially ready.

   ★★ One row on the day is not the same thing as one entry in the library, and the screen has to
   show that or the copy underneath makes no sense: the library holds the game, the day holds the
   rules and the scoring that applied THAT day. Editing the library never rewrites a day that has
   been played.

   ★ Inventing a game and putting it on the day are ONE act here. An organiser thinks "and then we
   play the blindfolded maze", not "first I will create a library entry"; the entry still happens,
   they just do not have to know it.

   ★ The count against the plan sits at the top, because a day generated with four games under a
   plan that promised six is short two rounds and nothing else on any screen says so.

   ★ Every field on an existing game saves on BLUR, one call per box, deliberately: this screen is
   read and tweaked between other things, and a Save button at the bottom of a list of six games is
   a button that gets forgotten with the rules half-typed. The invent form is the exception — it
   has one Save, because a game does not exist until it has a name.

   ★★ The PICTURE is the one thing here that is not only about this day: uploading it writes the
   library entry too, because this is the only screen a customer can put one on and a picture that
   lived on one evening would leave every event after it blank. It is also the one field that
   cannot be typed into the invent form — a blob is named after the row, and the row does not
   exist until the game is saved. So: write the game down, then give it a face. */

const CATEGORIES = ['endurance', 'teamwork', 'guts', 'skill'];

const EMPTY_DRAFT = {
  name: '', acronym: '', rules: '', scoring_type: '',
  endurance: '', teamwork: '', guts: '', skill: '',
};

const CompanyEventGames = () => {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [draft, setDraft] = useState(null);        // the "invent a game" form
  const [open, setOpen] = useState(null);          // which game on the day is expanded
  const [picking, setPicking] = useState(false);   // the library list

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/games`)
      .then(setData)
      .catch((e) => setNotice(apiErrorText(t, e)));
  }, [eventId, t]);

  useEffect(() => { load(); }, [load]);

  const call = (method, path, body) => {
    setBusy(true);
    setNotice(null);
    return apiRequest(method, `/company/event/${eventId}${path}`, body)
      .then((res) => { setData((d) => ({ ...d, ...res })); return res; })
      .catch((e) => { setNotice(apiErrorText(t, e)); throw e; })
      .finally(() => setBusy(false));
  };

  const invent = () => {
    if (!draft.name.trim()) { setNotice(t('gamesNameRequired')); return; }
    call('POST', '/games', draft).then(() => setDraft(null)).catch(() => {});
  };

  const addFromLibrary = (gameId) => {
    call('POST', '/games', { game_id: gameId }).then(() => setPicking(false)).catch(() => {});
  };

  const tune = (rowId, fields) => call('PUT', `/games/${rowId}`, fields).catch(() => {});

  /* The picture. Multipart, so it goes through apiFormRequest rather than ``call`` — the answer is
     the same whole payload every other write here comes back with, so the screen is redrawn the
     same way. */
  const uploadPhoto = (rowId, file) => {
    if (!file || busy) return;
    setBusy(true);
    setNotice(null);
    const body = new FormData();
    body.append('file', file);
    apiFormRequest(`/company/event/${eventId}/games/${rowId}/photo`, body)
      .then((res) => setData((d) => ({ ...d, ...res })))
      .catch((e) => setNotice(apiErrorText(t, e)))
      .finally(() => setBusy(false));
  };

  const dropPhoto = (rowId) => {
    // eslint-disable-next-line no-alert
    if (busy || !window.confirm(t('gamesPhotoRemoveConfirm'))) return;
    call('DELETE', `/games/${rowId}/photo`).catch(() => {});
  };

  if (!data) {
    return (
      <Base activeNav="home">
        <Card><p className="ct-field__hint">{notice || t('pleaseWait')}</p></Card>
      </Base>
    );
  }

  const games = data.games || [];
  const library = data.library || [];
  const scoringTypes = data.scoring_types || [];
  const canManage = !!data.can_manage;
  const locked = !canManage || busy;
  const needed = data.needed || 0;
  // What is in the library and NOT on this day — the only part of it worth offering.
  const onDay = new Set(games.map((g) => g.game_id));
  const spare = library.filter((g) => !onDay.has(g.id));

  const scoringName = (id) => (scoringTypes.find((s) => s.id === id) || {}).name;

  /* ★ The scoring type carries its input mask in the option itself ("Time — mm:ss"). The names
     are back-office vocabulary and unreadable on their own; the mask is the thing that tells an
     organiser what the marshall will actually be typing. */
  const scoringField = (id, value, onChange) => (
    <span className="ct-field">
      <label htmlFor={id}>{t('gamesScoring')}</label>
      <select id={id} value={value || ''} disabled={locked}
              onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('gamesScoringChoose')}</option>
        {scoringTypes.map((s) => (
          <option key={s.id} value={s.id}>{s.label ? `${s.name} — ${s.label}` : s.name}</option>
        ))}
      </select>
      <span className="ct-field__hint">{t('gamesScoringNote')}</span>
    </span>
  );

  /* The four sliders Machina has had for twenty years. Blank stays blank — an unanswered one is
     not a zero, and these feed the battle mix, not the score. */
  const categoryFields = (key, values, onChange) => (
    <div className="ct-cats">
      {CATEGORIES.map((field) => (
        <span key={field} className="ct-field ct-cats__one">
          <label htmlFor={`${key}-${field}`}>{t(`gamesCat_${field}`)}</label>
          <input id={`${key}-${field}`} type="number" min={0} max={10} step="1"
                 value={values[field] ?? ''} disabled={locked}
                 onChange={(e) => onChange(field, e.target.value)} />
        </span>
      ))}
    </div>
  );

  return (
    <Base activeNav="home">
      <Card>
        <Back to={`/company-event/${eventId}/tournament`} />
        <Title my="1" className="mt-3">
          <Icon name="running" theme="primary" className="me-2" />
          {t('gamesTitle')}
        </Title>

        {/* ★ The plan and the day, side by side. Two different facts and both are real: the number
            in the box sizes the day, the games below are what the schedule is built from. */}
        <p className="ct-sum">
          {needed
            ? t('gamesSummary', { count: games.length, needed })
            : t('gamesSummaryNoPlan', { count: games.length })}
        </p>
        {needed > 0 && games.length !== needed && (
          <p className="ce-note ce-note--warn">
            {games.length < needed
              ? t('gamesTooFew', { missing: needed - games.length })
              : t('gamesTooMany', { extra: games.length - needed })}
          </p>
        )}
        {notice && <p className="ce-note ce-note--warn">{notice}</p>}

        {/* ---- THE GAMES ON THIS DAY ---- */}
        {games.length === 0 && <p className="ct-field__hint">{t('gamesNoneYet')}</p>}

        {games.map((game, index) => (
          <div key={game.id} className="ct-team">
            <div className="ct-team__head">
              {/* ★ A thumbnail in the CLOSED row, not only in the open one: "which of my six
                  games still has no picture" is a question you answer by running your eye down a
                  list, and a list where you have to open six panels to find out answers it six
                  times over. */}
              {game.photo_url
                ? <img className="ct-game__thumb" src={game.photo_url} alt="" loading="lazy" />
                : (
                  <span className="ct-game__thumb ct-game__thumb--empty">
                    <Icon name="picture" />
                  </span>
                )}
              <button type="button" className={`ce-tab ${open === game.id ? 'is-on' : ''}`}
                      onClick={() => setOpen(open === game.id ? null : game.id)}>
                {game.name}
                {/* ★ The acronym is what the heats are named with ("A-TUG03"), so it belongs on
                    screen rather than staying a hidden detail of the schedule. */}
                <span className="ce-tab__n">{game.acronym}</span>
              </button>
              {game.scoring_type
                ? <span className="ce-pill">{scoringName(game.scoring_type)}</span>
                : <span className="ce-pill ce-pill--draft">{t('gamesNoScoring')}</span>}
              {canManage && (
                <span className="ce-person__acts">
                  <button type="button" className="ce-person__act" disabled={busy}
                          title={t('delete')}
                          onClick={() => call('DELETE', `/games/${game.id}`).catch(() => {})}>
                    <Icon name="trash" />
                  </button>
                </span>
              )}
            </div>

            {open === game.id && (
              <div className="ct-game__body">
                <div className="ct-game__pic">
                  <div className="ct-game__shot"
                       style={game.photo_url
                         ? { backgroundImage: `url("${game.photo_url}")` } : undefined}>
                    {!game.photo_url && <Icon name="picture" />}
                  </div>
                  {canManage && (
                    <div className="ct-game__picacts">
                      <span className="ct-field">
                        <label htmlFor={`photo-${game.id}`}>
                          {game.photo_url ? t('gamesPhotoReplace') : t('gamesPhotoAdd')}
                        </label>
                        {/* ★ The input is cleared after every pick. Without it, choosing the same
                            file twice — which is exactly what somebody does after a failed
                            upload — fires no change event at all and the screen looks dead. */}
                        <input id={`photo-${game.id}`} type="file" accept="image/*"
                               disabled={locked}
                               onChange={(e) => { uploadPhoto(game.id, e.target.files[0]);
                                                  e.target.value = ''; }} />
                      </span>
                      {game.photo_url && (
                        <button type="button" className="ct-linkbtn" disabled={busy}
                                onClick={() => dropPhoto(game.id)}>
                          {t('gamesPhotoRemove')}
                        </button>
                      )}
                      <span className="ct-field__hint">{t('gamesPhotoHint')}</span>
                    </div>
                  )}
                </div>

                <span className="ct-field">
                  <label htmlFor={`rules-${game.id}`}>{t('gamesRules')}</label>
                  <textarea id={`rules-${game.id}`} rows={5} defaultValue={game.rules || ''}
                            disabled={locked} placeholder={t('gamesRulesPlaceholder')}
                            onBlur={(e) => e.target.value !== (game.rules || '')
                              && tune(game.id, { rules: e.target.value })} />
                  <span className="ct-field__hint">{t('gamesRulesThisDayNote')}</span>
                </span>

                {scoringField(`scoring-${game.id}`, game.scoring_type,
                              (v) => tune(game.id, { scoring_type: v }))}

                {categoryFields(game.id, game,
                                (field, value) => tune(game.id, { [field]: value }))}

                <label className="ce-check mt-2">
                  <input type="checkbox" checked={game.is_water} disabled={locked}
                         onChange={(e) => tune(game.id, { is_water: e.target.checked })} />
                  {t('gamesWater')}
                  <span className="ce-check__hint">{t('gamesWaterHint')}</span>
                </label>
                <label className="ce-check">
                  <input type="checkbox" checked={game.is_joker} disabled={locked}
                         onChange={(e) => tune(game.id, { is_joker: e.target.checked })} />
                  {t('gamesJoker')}
                  <span className="ce-check__hint">{t('gamesJokerHint')}</span>
                </label>
                {/* ★★ Do the PLAYERS see this game? Not decoration: the list a team picks its
                    joker from is built from the games that have been announced, so an unannounced
                    game is one nobody can choose. Ticking the joker above announces it for that
                    reason; un-ticking the joker does not take it back off. */}
                <label className="ce-check">
                  <input type="checkbox" checked={game.is_public} disabled={locked}
                         onChange={(e) => tune(game.id, { is_public: e.target.checked })} />
                  {t('gamesVisible')}
                  <span className="ce-check__hint">{t('gamesVisibleHint')}</span>
                </label>
              </div>
            )}
          </div>
        ))}

        {/* ---- ADDING ONE ---- */}
        {canManage && !draft && (
          <div className="ce-actions mt-3">
            <button type="button" className="bravos-submit" disabled={busy}
                    onClick={() => { setDraft({ ...EMPTY_DRAFT }); setPicking(false); }}>
              <Icon name="plus" className="me-1" />{t('gamesInvent')}
            </button>
            {/* ★ Only once there is something in the library that is not already on the day. A
                button offering "0 games you already own" is a button that teaches nothing. */}
            {spare.length > 0 && (
              <button type="button" className="bravos-submit bravos-submit--ghost" disabled={busy}
                      onClick={() => setPicking(!picking)}>
                <Icon name="folder" /> {t('gamesFromLibrary', { n: spare.length })}
              </button>
            )}
          </div>
        )}

        {picking && spare.length > 0 && (
          <div className="ct-team mt-2">
            <p className="ct-pick__label">{t('gamesLibraryTitle')}</p>
            <p className="ct-field__hint">{t('gamesLibraryNote')}</p>
            <div className="ct-team__people">
              {spare.map((game) => (
                <button key={game.id} type="button" className="ct-chip ct-chip--pick"
                        disabled={busy} onClick={() => addFromLibrary(game.id)}>
                  {game.name}
                  <Icon name="plus" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ---- INVENTING ONE ---- */}
        {draft && (
          <div className="ct-team mt-3">
            <p className="ct-pick__label">{t('gamesInventTitle')}</p>
            <p className="ct-field__hint">{t('gamesInventNote')}</p>

            <span className="ct-field">
              <label htmlFor="game-name">{t('gamesName')}</label>
              <input id="game-name" maxLength={50} value={draft.name} disabled={busy}
                     onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </span>

            <span className="ct-field">
              <label htmlFor="game-acronym">{t('gamesAcronym')}</label>
              <input id="game-acronym" maxLength={3} value={draft.acronym} disabled={busy}
                     placeholder={t('gamesAcronymAuto')}
                     onChange={(e) => setDraft({ ...draft,
                                                 acronym: e.target.value.toUpperCase() })} />
              <span className="ct-field__hint">{t('gamesAcronymHint')}</span>
            </span>

            <span className="ct-field">
              <label htmlFor="game-rules">{t('gamesRules')}</label>
              <textarea id="game-rules" rows={5} value={draft.rules} disabled={busy}
                        placeholder={t('gamesRulesPlaceholder')}
                        onChange={(e) => setDraft({ ...draft, rules: e.target.value })} />
            </span>

            {scoringField('game-scoring', draft.scoring_type,
                          (v) => setDraft({ ...draft, scoring_type: v }))}

            {categoryFields('draft', draft,
                            (field, value) => setDraft({ ...draft, [field]: value }))}
            <span className="ct-field__hint">{t('gamesCatHint')}</span>

            <div className="ce-actions mt-3">
              <button type="button" className="bravos-submit" disabled={busy} onClick={invent}>
                {busy ? t('pleaseWait') : t('gamesSave')}
              </button>
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => setDraft(null)}>
                {t('cancel')}
              </button>
            </div>
          </div>
        )}

        <p className="ce-check__hint mt-3">{t('gamesNextStep')}</p>
      </Card>
    </Base>
  );
};

export default CompanyEventGames;
